<template>
	<div>
		<transition name="obm-fade">
			<div
				v-if="showModal"
				class="obm-main-bg d-flex justify-content-center w-100 invisible-scroll"
			>
				<div class="obm-progress-bar">
					<b-progress height="5px" :value="value"></b-progress>
				</div>
				<div
					class="obm-scroll-area d-flex justify-content-center w-100 invisible-scroll"
				>
					<div
						class="obm-main-content-container w-100"
						:class="{
							'obm-main-content-container--wide w-100': currentStep === 3,
						}"
					>
						<WelcomeStep
							v-if="currentStep === 1"
							@close="close"
							@next="goToDetails"
							@open-upload-resume="openUploadResume"
							:skipAnimation="skipAnimation"
							:firstName="firstName"
						/>
						<DetailsStep
							v-else-if="currentStep === 2"
							:selectedType="selectedType"
							@next="goToGoal"
							:skipDetailsAnimation="skipDetailsAnimation"
							:prefillData="detailsPrefillData"
							@back="goBackToWelcome"
						/>
						<GoalStep
							v-else-if="currentStep === 3"
							@back="gobackToDetail"
							@next="close"
							@explore="close"
						/>
					</div>
				</div>
			</div>
		</transition>

		<!-- Upload Resume modal — outside transition so Vue renders it independently -->
		<UploadResume
			v-if="showUploadResume"
			:showModal="showUploadResume"
			:showBackToOnboarding="true"
			@upload-modal-closed="showUploadResume = false"
			@back-to-onboarding="backToOnboarding"
			@open-fill-form="handleUploadOpenFillForm"
			@getCandidateDetail="handleUploadCandidateDetail"
			@upload-parse-complete="handleUploadParseComplete"
		/>
		<PersonalDetailsModal
			v-if="showPersonalDetailsModal"
			:showModal="showPersonalDetailsModal"
			:form="selectedPersonalDetailsForm"
			:formType="personalDetailsModalType"
			:fromOnboarding="true"
			@close-modal="handlePersonalDetailsModalClose"
			@update-data="fetchData(), handlePersonalDetailsUpdated()"
		/>
	</div>
</template>

<script>
import WelcomeStep from "./steps/WelcomeStep.vue";
import DetailsStep from "./steps/DetailsStep.vue";
import GoalStep from "./steps/GoalStep.vue";
import UploadResume from "@/components/BuilderPage/UploadResume.vue";
import "@/assets/globalcss/onboardingpage.css";
import PersonalDetailsModal from "@/components/BuilderPage/PersonalDetailsModal.vue";
import commonFunc from "@/components/mixin/commonFunction.js";
import onboardingMixin from "@/components/mixin/Onboarding.js";
import { trackOnboardingStarted } from "@/utils/posthogHelpers.js";
export default {
	emits: ["close"],
	props: {
		// "skills" → open DetailsStep (step 2), "goals" → open GoalStep (step 3)
		// null/undefined → start from WelcomeStep (step 1) as usual
		initialStep: {
			type: String,
			default: null,
		},
		firstName: {
			type: String,
			default: "",
		},
	},
	mixins: [commonFunc, onboardingMixin],
	data() {
		return {
			showModal: false,
			showUploadResume: false,
			currentStep: 1,
			selectedType: null,
			value: 33,
			skipAnimation: false,
			skipDetailsAnimation: false,
			// Prefill data received from GoalStep → Back API call.
			// Passed to DetailsStep so it can populate the form
			// without a second API call in its mounted().
			detailsPrefillData: null,
			// Level returned by the API after DetailsStep → Back.
			// Passed to WelcomeStep to highlight the matching section.
		};
	},
	components: {
		WelcomeStep,
		DetailsStep,
		GoalStep,
		UploadResume,
		PersonalDetailsModal,
	},
	watch: {
		initialStep: {
			immediate: true,
			handler(step) {
				if (step === "goals") {
					this.currentStep = 3;
					this.value = 100;
				} else if (step === "resumeUpload") {
					// Refresh restore: skip WelcomeStep and open UploadResume directly
					this.showModal = false;
					this.showUploadResume = true;
				} else {
					// "skills" and anything else → WelcomeStep
					this.currentStep = 1;
					this.value = 33;
				}
			},
		},
		showUploadResume(val) {
			this.$store.commit("candidateVUEX/setOnboardingActive", val);
		},
		showPersonalDetailsModal(val) {
			this.$store.commit("candidateVUEX/setOnboardingActive", val);
		},
	},
	mounted() {
		this.$nextTick(() => {
			if (this.initialStep !== "resumeUpload") {
				this.showModal = true;
			}
		});
		trackOnboardingStarted();
	},
	methods: {
		onResize() {
			this.windowWidth = window.innerWidth;
		},
		async fetchData() {
			this.showUploadResume = false;
			await this.$store.dispatch("userVUEX/fetchPostLoginData", true);
		},
		// DetailsStep → Back: receives the level from the API response.
		// Pass it to WelcomeStep so the correct section is highlighted.
		goBackToWelcome(level) {
			this.skipAnimation = true;

			this.detailsPrefillData = null; // clear stale prefill
			this.currentStep = 1;
			this.value = 33;
		},
		// GoalStep → Back: receives prefillData from the API response.
		// Store it so DetailsStep can hydrate its form without a second API call.
		gobackToDetail(prefillData) {
			this.skipDetailsAnimation = true;
			this.detailsPrefillData = prefillData ?? null;
			this.currentStep = 2;
			this.value = 67;
		},
		goToDetails(selectedType) {
			this.skipAnimation = false;
			this.selectedType = selectedType;
			this.currentStep = 2;
			this.value = 67;
		},
		goToGoal() {
			this.skipDetailsAnimation = false;
			this.currentStep = 3;
			this.value = 100;
		},
		openUploadResume() {
			this.showModal = false;
			this.showUploadResume = true;
		},
		backToOnboarding() {
			this.showUploadResume = false;
			this.showModal = true;
			this.currentStep = 1;
			this.value = 33;
		},
		close() {
			this.showModal = false;
			setTimeout(() => {
				this.$emit("close");
			}, 300);
		},
	},
};
</script>

<style scoped>
.obm-main-bg {
	position: fixed;
	top: 0px;
	left: 0;
	width: 100%;
	height: 100vh;
	z-index: 3000;
	background: rgba(27, 33, 44, 0.9);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.obm-progress-bar {
	width: 100%;
	flex-shrink: 0;
}

.obm-scroll-area {
	flex: 1;
	overflow-y: auto;
	overflow-x: hidden;
	padding-bottom: env(safe-area-inset-bottom, 0px);
}

/* Enter */
.obm-fade-enter-active,
.obm-fade-leave-active {
	transition: opacity 0.3s ease;
}

.obm-fade-enter,
.obm-fade-leave-to {
	opacity: 0;
}
/* .obm-main-content-container {
	width: 345px;
}
.obm-main-content-container--wide {
	width: 521px;
} */

@media (max-width: 1028px) {
	/* .obm-main-content-container,
	.obm-main-content-container--wide {
		width: 100%;
		padding: 0 16px;
	} */
	.obm-main-bg {
		height: 100dvh;
		top: env(safe-area-inset-top);
		padding-bottom: env(safe-area-inset-bottom);
		box-sizing: border-box;
	}
}
</style>

<style>
.obm-main-bg .progress {
	width: 100%;
	background: transparent;
	position: absolute;
	top: 2px;
	z-index: 3002;
}
.obm-main-bg .progress-bar {
	background: #ffffff;
}
</style>
